<template>
    <div class="d-flex w-100 card">
        <file-pond
            ref="pond"
            id="filepond"
            :files="files"
            :max-files="maxFiles"
            :accepted-file-types="acceptedFiles ? acceptedFiles.split(',') : null"
            :label-idle="$t('content.uploads.instruction')"
            :server="serverOptions"
            @addfile="fileAdded"
            @processfile="processFile"
            @removefile="fileRemoved"
            @updatefiles="updateFiles"
            :allow-multiple="false"
            :max-file-size="maxFileSize"
            :chunk-size="chunkSize"
        ></file-pond>
    </div>
</template>

<script>
import 'filepond/dist/filepond.min.css';
import FilePond, { setOptions } from 'vue-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileEncode from 'filepond-plugin-file-encode';
import FilePondPluginFileRename from 'filepond-plugin-file-rename';
import FilePondPluginFilePoster from 'filepond-plugin-file-poster';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import BaseButton from '../../../argon/components/BaseButton';

// Create the FilePond component and include plugins
const FilePondComponent = FilePond(
    FilePondPluginFileValidateType,
    FilePondPluginImagePreview,
    FilePondPluginFileValidateSize,
    FilePondPluginFileEncode,
    FilePondPluginFileRename,
    FilePondPluginFilePoster,
);

// Set FilePond options
setOptions({
    allowRevert: true,
    allowMultiple: false,
    maxFiles: 1,
    maxFileSize: '10000MB',
    chunkUploads: true,
    chunkForce: true,
    chunkSize: '10MB',
});
export default {
    emits: ['input', 'response', 'success', 'update:modelValue'],
    components: {
        BaseButton,
        FilePond: FilePondComponent,
    },
    name: 'UploadDropzone',
    props: {
        modelValue: {
            type: Array,
            required: true,
        },
        acceptedFiles: {
            type: String,
            default: null,
            required: false,
        },
        maxFiles: {
            type: Number,
            default: 1,
            required: false,
        },
        dropzoneOptions: {
            type: Object,
        },
        sendindEvent: Function,
        showMessage: Boolean,
    },
    data() {
        return {
            active: null,
            loading: false,
            clientSettings: {},
            files: [],
            maxFileSize: '10000MB', // Set maximum file size here
            chunkSize: '10MB', // Set chunk size here
            allowRevert: true, // Allow file revert if needed
            patchResponse: null, // Patch response
            serverOptions: {
                // Upload chunked server-side (chunks no Redis, destino S3).
                // Cada chunk vai num POST único p/ /api/upload-local/student,
                // evitando PUT direto cross-origin (CORS) e os métodos PATCH/HEAD do FilePond (405).
                process: (fieldName, file, metadata, load, error, progress, abort) => {
                    const chunkSize = 10 * 1024 * 1024; // 10MB
                    const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
                    const uploadId = (window.crypto && window.crypto.randomUUID)
                        ? window.crypto.randomUUID()
                        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
                    let aborted = false;
                    const settings = this.clientSettings;

                    const sendChunk = (index) => {
                        if (aborted) return;
                        const start = index * chunkSize;
                        const chunk = file.slice(start, Math.min(start + chunkSize, file.size));

                        const formData = new FormData();
                        formData.append('file', chunk, file.name);
                        formData.append('dzuuid', uploadId);
                        formData.append('dzchunkindex', index);
                        formData.append('dztotalchunkcount', totalChunks);
                        formData.append('dztotalfilesize', file.size);
                        formData.append('dzfilename', file.name);
                        formData.append('uploadHost', window.axios.APP_HOST);
                        formData.append('disk_space', settings.app.disk_space);
                        formData.append('disk_usage', settings.app.disk_usage);

                        window.axios.post('/api/upload-local/student', formData, {
                            onUploadProgress: (e) => {
                                progress(true, start + e.loaded, file.size);
                            },
                        })
                            .then((res) => {
                                if (res.data && res.data.errors) {
                                    error(res.data.errors);
                                    return;
                                }
                                if (index + 1 < totalChunks) {
                                    sendChunk(index + 1);
                                } else {
                                    load(JSON.stringify(res.data));
                                }
                            })
                            .catch((err) => {
                                error((err.response && err.response.data && err.response.data.errors)
                                    || err.message || 'Upload failed');
                            });
                    };

                    sendChunk(0);

                    return {
                        abort: () => {
                            aborted = true;
                            abort();
                        },
                    };
                },
                revert: null,
                remove: null,
            },
        };
    },
    beforeCreate() {
        this.clientSettings = this.$getSettings.clientSettings;
    },
    mounted() {
        this.clientSettings = this.$getSettings.clientSettings;
    },
    methods: {
        fileAdded(error, file) {
            if (error) {
                this.$notify({
                    type: 'danger',
                    message: 'Erro ao adicionar arquivo',
                });
                return;
            }
            this.files.push(file.file);
            this.$emit('update:modelValue', this.files); this.$emit('input', this.files);
        },
        processFile(error, file) {
            if (error) {
                this.$notify({
                    type: 'danger',
                    message: 'Erro ao processar arquivo',
                });
                return;
            }
            const res = JSON.parse(file.serverId);
            if (res.errors) {
                this.$notify({
                    type: 'danger',
                    message: 'Houve algum erro ao processar seu arquivo!',
                });
                return;
            }
            file.file.status = 'success';
            file.file.upload = {
                path: res.filePath,
                host: res.host,
                hash: res.hash,
                type: res.type,
                mime: res.mime,
                size: res.size,
                original_file: res.original_file,
                width: res.width,
                height: res.height,
                watermark: res.watermark,
                rate_limit: res.rate_limit,
                expire_date: res.expire_date,
                name: res.name,
                uploadLimits: res.uploadLimits,
                urls: res.urls,
            };
            if (res.uploadLimits) {
                this.clientSettings.app.disk_upload = res.uploadLimits.diskUpload;
            }
            this.$emit('success', file.file.upload);
            this.$emit('response', {
                status: 'success',
                response: res,
            });
            this.$notify({
                type: 'success',
                message: 'Arquivo enviado com sucesso!',
            });
            this.patchResponse = null;
        },
        fileRemoved(error, file) {
            if (error) {
                this.$notify({
                    type: 'danger',
                    message: 'Erro ao remover arquivo',
                });
                return;
            }
            this.deleteFile(file.file);
        },
        updateFiles(files) {
            this.files = files.map(fileItem => fileItem.file);
            this.$emit('update:modelValue', this.files); this.$emit('input', this.files);
        },
        deleteFile(file) {
            if (!file || !file.upload || !file.upload.hash) {
                this.files = this.files.filter(f => f !== file);
                this.$emit('update:modelValue', this.files); this.$emit('input', this.files);
                return Promise.resolve();
            }
            this.loading = true;
            return window.axios
                .delete(
                    `/api/upload-local/delete/${file.upload.hash}`,
                    {
                        params: {
                            uploadHost: window.axios.APP_HOST,
                            disk_space: this.clientSettings.app.disk_space,
                            disk_usage: this.clientSettings.app.disk_usage,
                        },
                    },
                )
                .then(() => {
                    this.files = this.files.filter(f => f && f.upload && f.upload.hash !== file.upload.hash);
                    this.loading = false;
                    this.$emit('update:modelValue', this.files); this.$emit('input', this.files);
                    this.$notify({
                        type: 'success',
                        message: 'Arquivo removido com sucesso!',
                    });
                })
                .catch(() => {
                    this.loading = false;
                    this.$notify({
                        type: 'danger',
                        message: 'Erro ao remover arquivo',
                    });
                });
        },
    },
};
</script>

<style>
/* Custom Style For FilePond */
#filepond {
    border: 2px dashed color-mix(in srgb, rgb(var(--v-theme-info)) 100%, white 20%);
    background-color: rgb(var(--v-theme-info-secondary));
}

.filepond--credits
{
    display: none;
}
</style>
